import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type FontMode = 'inter' | 'poppins' | 'playfair' | 'space-grotesk' | 'plus-jakarta';
export type ThemePreset = 'light' | 'dark' | 'ocean' | 'sunset' | 'forest' | 'purple' | 'minimal' | 'cyber' | 'amber' | 'emerald' | 'cobalt' | 'charcoal' | 'custom';

export interface ThemeColors {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  primary: string;
  primaryForeground: string;
  border: string;
  selectionColor: string;
  selectionTextColor: string;
  selectionColorOpacity: number;
  selectionTextColorOpacity: number;
}

export interface ThemeSettings {
  fontMode: FontMode;
  preset: ThemePreset;
  contentScale: number; // 0.8 to 1.4
  colors: ThemeColors;
}

interface ThemeContextType {
  theme: ThemeSettings;
  setThemePreset: (preset: ThemePreset) => void;
  setContentScale: (scale: number) => void;
  setCustomColor: (key: keyof ThemeColors, value: string | number) => void;
  resetTheme: () => void;
  setFontMode: (mode: FontMode) => void;
}

const defaultColors: ThemeColors = {
  background: '#ffffff',
  foreground: '#0f172a', // Slate 900 - High Contrast
  card: '#ffffff',
  cardForeground: '#0f172a',
  primary: '#0284c7', // Sky 600
  primaryForeground: '#ffffff',
  border: '#e2e8f0',
  selectionColor: '#e0f2fe', // Sky 100
  // Selection text color should default to main text color
  selectionTextColor: '#0f172a',
  selectionColorOpacity: 100,
  selectionTextColorOpacity: 100,
};

const presets: Record<string, Partial<ThemeColors>> = {
  light: defaultColors,
  dark: {
    background: '#000000', // True Black for OLED/Modern feel
    foreground: '#ffffff', // Pure White for max contrast
    // Keep card same as page background
    card: '#000000',
    cardForeground: '#ffffff',
    primary: '#38bdf8', // Sky 400 - Bright & Visible
    primaryForeground: '#000000',
    border: '#27272a', // Zinc 800
    selectionColor: '#0c4a6e', // Sky 900
    // Match selection text to main text color for this theme
    selectionTextColor: '#ffffff',
  },
  ocean: {
    background: '#020617', // Slate 950
    foreground: '#f1f5f9', // Slate 100
    // Keep card same as page background
    card: '#020617',
    cardForeground: '#f1f5f9',
    primary: '#0ea5e9', // Sky 500
    primaryForeground: '#ffffff',
    border: '#1e293b',
    selectionColor: '#1e293b',
    selectionTextColor: '#f1f5f9',
  },
  sunset: {
    background: '#2a0a04', // Deep Red-Orange
    foreground: '#fff7ed', // Orange 50
    // Keep card same as page background
    card: '#2a0a04',
    cardForeground: '#fff7ed',
    primary: '#fb923c', // Orange 400
    primaryForeground: '#431407',
    border: '#7c2d12',
    selectionColor: '#7c2d12',
    selectionTextColor: '#fff7ed',
  },
  forest: {
    background: '#022c22', // Teal 950
    foreground: '#ecfdf5', // Teal 50
    // Keep card same as page background
    card: '#022c22',
    cardForeground: '#ecfdf5',
    primary: '#34d399', // Emerald 400
    primaryForeground: '#022c22',
    border: '#065f46',
    selectionColor: '#064e3b',
    selectionTextColor: '#ecfdf5',
  },
  purple: {
    background: '#1e1b4b', // Indigo 950
    foreground: '#eef2ff', // Indigo 50
    // Keep card same as page background
    card: '#1e1b4b',
    cardForeground: '#eef2ff',
    primary: '#818cf8', // Indigo 400
    primaryForeground: '#1e1b4b',
    border: '#3730a3',
    selectionColor: '#312e81',
    selectionTextColor: '#eef2ff',
  },
  minimal: {
    background: '#f8fafc', // Slate 50
    foreground: '#020617', // Slate 950
    // Keep card same as page background
    card: '#f8fafc',
    cardForeground: '#020617',
    primary: '#475569', // Slate 600
    primaryForeground: '#ffffff',
    border: '#cbd5e1',
    selectionColor: '#e2e8f0',
    selectionTextColor: '#0f172a',
    selectionColorOpacity: 100,
    selectionTextColorOpacity: 100,
  },
  cyber: {
    background: '#0a0e1a', // Very dark blue-black
    foreground: '#e0f2fe', // Cyan 100
    // Keep card same as page background
    card: '#0a0e1a',
    cardForeground: '#e0f2fe',
    primary: '#06b6d4', // Cyan 500 - bright neon cyan
    primaryForeground: '#0a0e1a',
    border: '#1e293b', // Slate 800
    selectionColor: '#164e63', // Cyan 800
    selectionTextColor: '#e0f2fe', // match foreground
  },
  amber: {
    background: '#1c1917', // Warm dark brown
    foreground: '#fffbeb', // Amber 50
    // Keep card same as page background
    card: '#1c1917',
    cardForeground: '#fffbeb',
    primary: '#fbbf24', // Amber 400 - golden yellow
    primaryForeground: '#1c1917',
    border: '#44403c', // Stone 700
    selectionColor: '#78350f', // Amber 900
    selectionTextColor: '#fffbeb',
  },
  emerald: {
    background: '#022c22', // Emerald 950
    foreground: '#ecfdf5', // Emerald 50
    // Keep card same as page background
    card: '#022c22',
    cardForeground: '#ecfdf5',
    primary: '#10b981', // Emerald 500 - vibrant green
    primaryForeground: '#022c22',
    border: '#065f46', // Emerald 800
    selectionColor: '#065f46',
    selectionTextColor: '#ecfdf5', // match foreground
  },
  cobalt: {
    background: '#0c1226', // Deep navy
    foreground: '#eff6ff', // Blue 50
    // Keep card same as page background
    card: '#0c1226',
    cardForeground: '#eff6ff',
    primary: '#3b82f6', // Blue 500 - bright cobalt blue
    primaryForeground: '#0c1226',
    border: '#1e40af', // Blue 700
    selectionColor: '#1e40af',
    selectionTextColor: '#eff6ff', // match foreground
  },
  charcoal: {
    background: '#0f0f0f', // Almost black
    foreground: '#f5f5f5', // Neutral 100
    // Keep card same as page background
    card: '#0f0f0f',
    cardForeground: '#f5f5f5',
    primary: '#a3a3a3', // Neutral 400 - cool gray
    primaryForeground: '#0f0f0f',
    border: '#404040', // Neutral 700
    selectionColor: '#404040',
    selectionTextColor: '#f5f5f5', // match foreground
  }
};

const defaultTheme: ThemeSettings = {
  fontMode: 'inter',
  preset: 'light',
  // Default text size: 90% across all themes
  contentScale: 0.9,
  colors: defaultColors,
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'metefy_theme_settings_v2';

// Helper to convert hex to HSL for Tailwind CSS variables if needed, 
// but since we override the CSS variables directly with hex values (or their equivalent),
// we might need to be careful if Tailwind expects HSL.
// Actually, Tailwind 3.x with --background expects HSL values (without hsl() wrapper) IF defined as such in index.css.
// In our index.css: --background: 210 40% 98%; (HSL values without hsl())
// So we need a converter from Hex to HSL values string "H S% L%".

function hexToHsl(hex: string): string {
  if (!hex) return '0 0% 100%'; // Fallback

  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt("0x" + hex[1] + hex[1]);
    g = parseInt("0x" + hex[2] + hex[2]);
    b = parseInt("0x" + hex[3] + hex[3]);
  } else if (hex.length === 7) {
    r = parseInt("0x" + hex[1] + hex[2]);
    g = parseInt("0x" + hex[3] + hex[4]);
    b = parseInt("0x" + hex[5] + hex[6]);
  }
  r /= 255;
  g /= 255;
  b /= 255;
  const cmin = Math.min(r,g,b),
      cmax = Math.max(r,g,b),
      delta = cmax - cmin;
  let h = 0, s = 0, l = 0;

  if (delta === 0)
    h = 0;
  else if (cmax === r)
    h = ((g - b) / delta) % 6;
  else if (cmax === g)
    h = (b - r) / delta + 2;
  else
    h = (r - g) / delta + 4;

  h = Math.round(h * 60);

  if (h < 0) h += 360;

  l = (cmax + cmin) / 2;
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);

  return `${h} ${s}% ${l}%`;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemeSettings>(defaultTheme);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load theme from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Deep merge logic for colors to ensure we don't lose default colors if stored data is partial
        const mergedColors = { ...defaultTheme.colors, ...(parsed.colors || {}) };
        
        // Ensure all required color keys exist in mergedColors, fallback to default if missing
        Object.keys(defaultColors).forEach(key => {
          if (!mergedColors[key as keyof ThemeColors]) {
             // @ts-ignore
            mergedColors[key as keyof ThemeColors] = defaultColors[key as keyof ThemeColors];
          }
        });

        setTheme({ ...defaultTheme, ...parsed, colors: mergedColors });
      }
    } catch (error) {
      console.error('Error loading theme from localStorage:', error);
    }
    setIsInitialized(true);
  }, []);

  // Apply theme to document
  useEffect(() => {
    if (!isInitialized) return;

    const root = document.documentElement;
    
    // Apply font mode
    root.classList.remove('font-inter', 'font-poppins', 'font-playfair', 'font-space-grotesk', 'font-plus-jakarta');
    root.classList.add(`font-${theme.fontMode}`);
    
    // Apply content scale (font-size on html)
    // Default 16px = 100%. Range 0.8 to 1.4 -> 80% to 140%
    root.style.fontSize = `${theme.contentScale * 100}%`;

    // Apply colors to CSS variables
    // We need to update the core variables that Tailwind uses
    const vars = {
      '--background': hexToHsl(theme.colors.background),
      '--foreground': hexToHsl(theme.colors.foreground),
      '--card': hexToHsl(theme.colors.card),
      '--card-foreground': hexToHsl(theme.colors.cardForeground),
      '--primary': hexToHsl(theme.colors.primary),
      '--primary-foreground': hexToHsl(theme.colors.primaryForeground),
      '--border': hexToHsl(theme.colors.border),
      // Selection colors (custom handling as before)
    };

    Object.entries(vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Handle Selection Colors (which use rgba/opacity logic in index.css)
    const hexToRgba = (hex: string, opacity: number): string => {
      if (!hex || typeof hex !== 'string') return 'rgba(0, 0, 0, 0)';
      
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      const alpha = opacity / 100;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    root.style.setProperty('--selection-color', hexToRgba(theme.colors.selectionColor, theme.colors.selectionColorOpacity));
    // Selection text color comes from theme.colors.selectionTextColor (can be customized in Appearance drawer)
    root.style.setProperty('--selection-text-color', hexToRgba(theme.colors.selectionTextColor, theme.colors.selectionTextColorOpacity));
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
  }, [theme, isInitialized]);

  const setThemePreset = (preset: ThemePreset) => {
    if (preset === 'custom') {
      setTheme(prev => ({ ...prev, preset: 'custom' }));
      return;
    }

    const presetColors = presets[preset];
    if (presetColors) {
      setTheme(prev => ({
        ...prev,
        preset,
        colors: { ...prev.colors, ...presetColors }
      }));
    }
  };

  const setContentScale = (scale: number) => {
    setTheme(prev => ({ ...prev, contentScale: scale }));
  };

  const setCustomColor = (key: keyof ThemeColors, value: string | number) => {
    setTheme(prev => ({
      ...prev,
      preset: 'custom',
      colors: { ...prev.colors, [key]: value }
    }));
  };
  
  const setFontMode = (mode: FontMode) => {
    setTheme(prev => ({ ...prev, fontMode: mode }));
  };

  const resetTheme = () => {
    setTheme(defaultTheme);
  };

  // Compatibility helpers for old API if needed, or just new API
  return (
    <ThemeContext.Provider value={{ 
      theme, 
      setThemePreset, 
      setContentScale, 
      setCustomColor, 
      resetTheme,
      setFontMode
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
